import React, { useState, useEffect, useCallback } from 'react';
import { Volume2, X, MessageCircle, Sparkles } from 'lucide-react';
import { apiClient } from '../../api/client';

const TONE_COLORS = {
  warm: 'from-amber-500 to-orange-500',
  playful: 'from-pink-500 to-purple-500',
  motivational: 'from-blue-500 to-indigo-600',
  gentle: 'from-violet-400 to-purple-400',
  celebratory: 'from-yellow-400 to-amber-500',
  curious: 'from-teal-400 to-cyan-500',
};

const VoiceNoteCard = ({ onDismissAll }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotes = useCallback(async () => {
    try {
      const data = await apiClient.request('/api/advanced-engagement/voice-notes');
      setNotes(data.notes || []);
    } catch {
      setNotes([]);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const dismissNote = async (noteId) => {
    try {
      await apiClient.request(`/api/advanced-engagement/voice-notes/${noteId}/dismiss`, { method: 'POST' });
      setNotes(prev => prev.filter(n => n.note_id !== noteId));
      if (notes.length <= 1 && onDismissAll) onDismissAll();
    } catch {}
  };

  const playNote = async (noteId) => {
    try {
      await apiClient.request(`/api/advanced-engagement/voice-notes/${noteId}/played`, { method: 'POST' });
      setNotes(prev => prev.map(n => n.note_id === noteId ? { ...n, played: true } : n));
    } catch {}
  };

  if (loading || notes.length === 0) return null;

  const note = notes[0]; // Show most recent
  const gradient = TONE_COLORS[note.tone] || TONE_COLORS.warm;

  return (
    <div
      className="mx-3 sm:mx-4 mb-3 p-3 sm:p-4 rounded-xl border border-gray-200 bg-white shadow-sm animate-in slide-in-from-top-2"
      data-testid="voice-note-card"
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
          <MessageCircle size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1">
            <Sparkles size={12} className="text-amber-500" />
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">AvA Voice Note</span>
          </div>
          <p className="text-sm text-gray-800 leading-relaxed">{note.text}</p>
        </div>
        <button
          onClick={() => dismissNote(note.note_id)}
          className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
          data-testid="voice-note-dismiss"
        >
          <X size={16} />
        </button>
      </div>
      <div className="flex items-center gap-2 mt-3 ml-13">
        <button
          onClick={() => playNote(note.note_id)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
            note.played
              ? 'bg-gray-100 text-gray-500'
              : 'bg-gradient-to-r ' + gradient + ' text-white hover:shadow-md'
          }`}
          data-testid="voice-note-play"
        >
          <Volume2 size={14} />
          {note.played ? 'Heard' : 'Listen'}
        </button>
        {notes.length > 1 && (
          <span className="text-xs text-gray-400">+{notes.length - 1} more</span>
        )}
      </div>
    </div>
  );
};

export default VoiceNoteCard;
